import { createFileRoute, Link } from "@tanstack/react-router";
import { CtaLink } from "@/components/marketing/CtaLink";
import { ArrowRight, Briefcase, Fingerprint, Sparkles, Users, Sparkles as Spark } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { SiteLayout } from "@/components/marketing/SiteLayout";
import { SiteBreadcrumbs } from "@/components/marketing/SiteBreadcrumbs";
import { alternateLinkList } from "@/lib/i18n/locale";
import heroImg from "@/assets/hero-card.webp";

export const Route = createFileRoute("/vi/platform/")({
  head: () => ({
    meta: [
      { title: "Nền tảng — banana.cards" },
      { name: "description", content: "Nền tảng Banana đầy đủ: danh tính & chia sẻ kỹ thuật số, bộ AI, CRM tích hợp và các ứng dụng kinh doanh hàng ngày biến mỗi lần chạm thành doanh thu." },
      { property: "og:title", content: "Nền tảng — banana.cards" },
      { property: "og:description", content: "Danh tính & chia sẻ, AI, CRM, và các ứng dụng vận hành doanh nghiệp — một nền tảng phát triển cùng bạn." },
      { property: "og:image", content: heroImg },
      { property: "og:locale", content: "vi_VN" },
    ],
    links: alternateLinkList("/platform", "vi"),
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Nền tảng Banana",
          description: "Nền tảng Banana đầy đủ: danh tính & chia sẻ kỹ thuật số, bộ AI, CRM tích hợp và các ứng dụng kinh doanh hàng ngày.",
          url: "https://banana.cards/vi/platform",
          isPartOf: { "@type": "WebSite", name: "banana.cards", url: "https://banana.cards" },
        }),
      },
    ],
  }),
  component: Page,
});

type Pillar = {
  title: string;
  href: string;
  icon: LucideIcon;
  desc: string;
  links: { label: string; href: string }[];
};

const pillars: Pillar[] = [
  {
    title: "Danh tính & Chia sẻ",
    href: "/vi/platform/identity-and-sharing",
    icon: Fingerprint,
    desc: "Danh thiếp kỹ thuật số, sản phẩm NFC cao cấp và mọi kênh chia sẻ — QR, Wallet, liên kết, chữ ký email.",
    links: [
      { label: "Danh thiếp kỹ thuật số", href: "/vi/platform/cards" },
      { label: "Sản phẩm NFC", href: "/vi/platform/nfc" },
      { label: "Kết nối & chia sẻ", href: "/vi/platform/networking" },
    ],
  },
  {
    title: "Bộ AI",
    href: "/vi/platform/ai-suite",
    icon: Sparkles,
    desc: "Bộ não AI ghi nhớ mọi liên hệ, cùng với làm giàu dữ liệu và công cụ viết, dịch, chấm điểm cho bạn.",
    links: [
      { label: "Bộ nhớ AI Brain", href: "/vi/platform/ai" },
      { label: "Tự động làm giàu dữ liệu", href: "/vi/platform/ai-enrichment" },
      { label: "Viết, dịch, chấm điểm", href: "/vi/platform/ai-tools" },
    ],
  },
  {
    title: "CRM",
    href: "/vi/platform/crm",
    icon: Users,
    desc: "Ghi nhận mọi khách hàng tiềm năng, di chuyển giao dịch qua pipeline thông minh và xem điều gì đang hiệu quả theo thời gian thực.",
    links: [
      { label: "Liên hệ & ghi nhận khách hàng", href: "/vi/platform/contacts" },
      { label: "Pipeline & nhiệm vụ", href: "/vi/platform/pipelines" },
      { label: "Phân tích thời gian thực", href: "/vi/platform/analytics" },
    ],
  },
  {
    title: "Vận hành doanh nghiệp",
    href: "/vi/platform/run-your-business",
    icon: Briefcase,
    desc: "Đặt lịch, họp, hợp đồng, tự động hóa, đánh giá, sự kiện, nhiệm vụ — cài đặt chỉ trong một cú nhấp.",
    links: [
      { label: "Đặt lịch & họp", href: "/vi/platform/bookings" },
      { label: "Hợp đồng & ký điện tử", href: "/vi/platform/contracts" },
      { label: "Tự động hóa & đánh giá", href: "/vi/platform/automations" },
    ],
  },
];

function Page() {
  return (
    <SiteLayout>
      <section className="bg-background">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 pb-12 pt-20 md:grid-cols-2 md:px-6">
          <div className="flex flex-col justify-center order-2 md:order-1">
            <SiteBreadcrumbs className="mb-6" currentName="Nền tảng Banana" />
            <h1 className="font-display text-3xl leading-[1.1] md:text-5xl">
              Bốn bộ sản phẩm, <em className="italic">một hệ điều hành cho mạng lưới của bạn.</em>
            </h1>
            <p className="mt-5 max-w-md text-base text-muted-foreground md:text-lg">
              Banana được xây dựng trên bốn trụ cột: danh tính & cách bạn chia sẻ, bộ não AI ghi nhớ mọi liên hệ, CRM biến cuộc trò chuyện thành pipeline, và các ứng dụng vận hành doanh nghiệp.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground transition hover:scale-[1.02]">
                Bắt đầu — miễn phí <ArrowRight className="h-4 w-4" />
              </CtaLink>
              <Link to="/vi/pricing" className="inline-flex items-center gap-2 rounded-xl bg-muted px-6 py-3 text-sm font-bold transition hover:bg-muted/70">
                Xem giá
              </Link>
            </div>
          </div>
          <div className="relative order-1 md:order-2">
            <div className="absolute -inset-6 rounded-[2rem] bg-brand-lime/10 blur-3xl" aria-hidden />
            <img src={heroImg} alt="Nền tảng Banana" width={1600} height={1024} className="relative w-full rounded-2xl object-cover" />
          </div>
        </div>
      </section>

      <section className="bg-muted">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6">
          <div className="mb-10 max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">Những gì bên trong</p>
            <h2 className="mt-2 font-display text-3xl leading-[1.1] md:text-[2.5rem]">
              Mọi thứ bạn cần, <em className="italic">trong một nơi.</em>
            </h2>
            <p className="mt-4 text-muted-foreground">
              Nhấp vào tiêu đề trụ cột để xem tổng quan đầy đủ — hoặc đi thẳng đến công cụ cụ thể.
            </p>
          </div>
          <div className="grid gap-5 md:grid-cols-2">
            {pillars.map((p) => {
              const Icon = p.icon;
              return (
                <article key={p.href} className="flex flex-col rounded-2xl bg-card p-7 md:p-8">
                  <div className="flex items-start justify-between gap-4">
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
                      <Icon className="h-5 w-5" />
                    </span>
                    <Link
                      to={p.href}
                      className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1.5 text-xs font-bold text-foreground transition hover:bg-foreground hover:text-background"
                      aria-label={`Mở tổng quan ${p.title}`}
                    >
                      Tổng quan <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  </div>

                  <h3 className="mt-6 font-display text-2xl leading-tight md:text-3xl">
                    <Link to={p.href} className="underline-offset-4 hover:underline">
                      {p.title}
                    </Link>
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground md:text-base">{p.desc}</p>

                  <div className="mt-6 border-t border-muted-foreground/10 pt-5">
                    <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
                      Đi thẳng đến
                    </p>
                    <ul className="grid gap-2">
                      {p.links.map((l) => (
                        <li key={l.href}>
                          <Link
                            to={l.href}
                            className="group flex items-center justify-between gap-3 rounded-xl bg-muted px-4 py-3 text-sm font-bold transition hover:bg-foreground hover:text-background"
                          >
                            <span>{l.label}</span>
                            <ArrowRight className="h-4 w-4 transition group-hover:translate-x-0.5" />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-background">
        <div className="mx-auto max-w-7xl px-4 pb-24 pt-20 md:px-6">
          <div className="relative overflow-hidden rounded-3xl bg-foreground px-8 py-16 text-center text-background md:p-20">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,oklch(0.96_0.21_117/0.15),transparent_50%)]" aria-hidden />
            <Spark className="mx-auto h-6 w-6 text-brand-lime" />
            <h2 className="mx-auto mt-5 max-w-2xl font-display text-3xl leading-[1.1] md:text-5xl">
              Một nền tảng. <em className="italic text-brand-lime">Mọi giai đoạn của mối quan hệ.</em>
            </h2>
            <p className="mx-auto mt-5 max-w-lg text-base text-background/70">
              Từ khoảnh khắc bạn chạm để chia sẻ thẻ đến ngày chốt giao dịch — Banana giữ trọn ngữ cảnh.
            </p>
            <div className="relative mt-8 flex flex-wrap items-center justify-center gap-3">
              <CtaLink to="/signup" className="inline-flex items-center gap-2 rounded-xl bg-brand-lime px-6 py-3 text-sm font-bold text-brand-lime-foreground">
                Bắt đầu — miễn phí
              </CtaLink>
              <Link to="/vi/contact" className="inline-flex items-center gap-2 rounded-xl border border-background/20 px-6 py-3 text-sm font-bold">
                Liên hệ bán hàng
              </Link>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}